import {useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

import {useQuery} from '@apollo/client/react';
import {StatsCard} from '../components/StatsCard';
import {DateWithRelative} from '../components/DateWithRelative';
import {gql} from 'types/__generated__';

const ORGANISATIONS_QUERY = gql(`
  query OrphanedFilesOrganisations {
    organisations {
      id
      name
    }
  }
`);

const ORPHANED_FILES_QUERY = gql(`
  query OrphanedFiles($organisationId: ID) {
    orphanedFiles(organisationId: $organisationId) {
      fileId
      organisationId
      name
      sizeBytes
      createdAt
    }
  }
`);

function formatBytes(bytes: number | null | undefined): string {
  if (bytes === 0) return '0 B';
  // sizeBytes is optional in the schema; undefined used to reach Math.log and render
  // "NaN undefined" in the table.
  if (bytes === null || bytes === undefined) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

export default function OrphanedFilesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const organisationId = searchParams.get('org') || '';

  const {data: orgData} = useQuery(ORGANISATIONS_QUERY, {fetchPolicy: 'cache-first'});
  // The schema types this as [Organisation] — nullable elements — so drop the holes once
  // here and let the generated row type carry through.
  const organisations = (orgData?.organisations ?? []).filter((o) => o !== null);
  const orgNameById = useMemo(() => {
    const map = new Map<string, string>();
    organisations.forEach((o) => map.set(o.id, o.name));
    return map;
  }, [organisations]);

  const {loading, error, data} = useQuery(ORPHANED_FILES_QUERY, {
    variables: {organisationId: organisationId || undefined},
    fetchPolicy: 'cache-and-network',
  });

  const files = data?.orphanedFiles ?? [];
  const totalSize = files.reduce((sum, f) => sum + (f.sizeBytes ?? 0), 0);

  const setOrg = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('org', value);
      else next.delete('org');
      return next;
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Orphaned Files</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Files that no longer have any reference and are older than the 30-day grace window. Read-only — review before any cleanup.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organisation</span>
          <select
            value={organisationId}
            onChange={(e) => setOrg(e.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 min-w-[16rem]"
          >
            <option value="">All organisations</option>
            {[...organisations]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatsCard label="Orphaned Files" value={loading && files.length === 0 ? '...' : files.length} />
        <StatsCard label="Reclaimable Size" value={loading && files.length === 0 ? '...' : formatBytes(totalSize)} />
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <h3 className="text-red-800 dark:text-red-200 font-semibold">Could not load orphaned files</h3>
          <p className="text-red-600 dark:text-red-300 mt-1">{error.message}</p>
        </div>
      )}

      {loading && files.length === 0 && !error && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {!error && files.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            {/* Fixed layout so a long unbreakable filename can't push the other columns
                out of view; Name takes the remaining width and truncates. */}
            <table className="w-full min-w-[64rem] table-fixed divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="w-48 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organisation</th>
                  <th className="w-24 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Size</th>
                  <th className="w-44 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="w-72 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">File ID</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {files.map((file) => {
                  const orgName = file.organisationId
                    ? orgNameById.get(file.organisationId) ?? file.organisationId
                    : null;
                  return (
                    <tr key={file.fileId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100 truncate" title={file.name ?? undefined}>
                        {file.name || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300 truncate" title={orgName ?? undefined}>
                        {orgName ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatBytes(file.sizeBytes)}</td>
                      <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300">
                        <DateWithRelative timestamp={file.createdAt} />
                      </td>
                      <td className="px-6 py-3 text-xs font-mono text-gray-400 dark:text-gray-500 whitespace-nowrap">{file.fileId}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && files.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">No orphaned files found.</div>
      )}
    </div>
  );
}
